import { createContext, useContext } from 'react';
import type {
  Report,
  ReportView,
  Company,
  Site,
  ReportTemplate,
  ReportSectionComment,
} from '@/data/schemas';
import type { ActionRow } from '@/data/db';
import type { EntityFieldDescriptor } from '@/features/reports/components/fields/FieldRenderer';
import type { ReportCurrentRoute, ReportNavigation } from './types';
import type { ReportLockState } from '@/data/repos/reportsRepo';

export interface ReportDetailContextValue {
  report: Report;
  effectiveReport: Report;
  template: ReportTemplate | null;
  company: Company | null;
  site: Site | null;
  view: ReportView;
  nav: ReportNavigation;
  isDraft: boolean;
  isEditMode: boolean;
  schedulingWarning?: {
    hasWarning: boolean;
    isPlannedStale: boolean;
    isReportOutsideWindow: boolean;
    isPlannedMismatch: boolean;
  };
  onApplyPlannedToReport?: () => void;
  onApplyReportToPlanned?: () => void;
  lockState?: ReportLockState | null;
  lockMessage?: string | null;
  onToggleEdit: () => void;
  onFinalizeDraft: () => void;
  onDeleteDraft: () => void;
  isFinalizing: boolean;
  isDeletingDraft: boolean;
  onOpenNewModal: () => void;
  onCloseNewModal: () => void;
  isNewModalOpen: boolean;
  isPdfLoading: boolean;
  pdfError: string | null;
  onPreviewPdf: () => Promise<void>;
  onGeneratePdf: () => Promise<void>;
  onRetryPdf: () => void;
  onDismissPdfError: () => void;
  onSaveSection: () => void;
  onFieldChange: (
    sectionId: string,
    rowItemId: string | undefined,
    fieldId: string,
    value: unknown
  ) => void;
  onSectionCommentChange: (
    sectionId: string,
    comment: Omit<ReportSectionComment, 'updatedAt'> | null
  ) => void;
  onEntityFieldRequest: (descriptor: EntityFieldDescriptor) => void;
  onSettingsFieldRequest: () => void;
  currentSection: ReportCurrentRoute;
  activeAnchor?: string;
  setActiveAnchor: (anchor?: string) => void;
  actions: ActionRow[];
}

const ReportDetailContext = createContext<ReportDetailContextValue | null>(null);

export function useReportDetailContext(): ReportDetailContextValue {
  const ctx = useContext(ReportDetailContext);
  if (!ctx) {
    throw new Error('ReportDetailContext missing in tree');
  }
  return ctx;
}

export { ReportDetailContext };
